import { GlobalStateController } from "bai-react-global-state";

import { FormatType, TextStyleType } from "../../../../common/constants";
import { formatter } from "../../../../common/formatter";
import { CategoryController } from "../../../../common/SlideCategories/CategoryController";
import { findElementsWithImages } from "../../../../common/utils/findElementsWithImages";
import { applySlideImage, resetSlideImage } from "../../../core/models/slide";
import AiGenerationService from "../../../core/services/aiGeneration";
import { ShowWarningDialog } from "../../../react/components/Dialogs/BaseDialog";
import { _ } from "../../../vendor";

const initialState = {
    currentSlide: null,
    generatedIdeas: [],
    isGenerating: false,
    slideCategories: [],
    slideImage: null
};

class SlideAiController extends GlobalStateController {
    get currentSlide() {
        return this._state.currentSlide;
    }

    async setCurrentSlide(slide, canvasController) {
        if (this._state.currentSlide) {
            this._state.currentSlide.off("change:metadata", this.onMetadataUpdated);
        }

        let metadata = slide.getMetadata();

        await this._updateState({
            currentSlide: slide,
            canvasController,
            generatedIdeas: metadata.generatedIdeas || [],
            slideImage: metadata.generatedModel?.slideImage || null,
            slideCategories: [],
            isGenerating: false
        });

        if (!metadata.generatedIdeas) {
            slide.on("change:metadata", this.onMetadataUpdated);
        }

        this.buildSlideModels();
    }

    onMetadataUpdated = async slide => {
        if (this._state.currentSlide !== slide) {
            return;
        }

        const metadata = slide.getMetadata();

        this._updateState({
            generatedIdeas: metadata.generatedIdeas || [],
            slideImage: metadata.generatedModel?.slideImage || null
        });

        this.buildSlideModels();

        slide.off("change:metadata", this.onMetadataUpdated);
    }

    buildPromptFromSlide(canvas) {
        let prompt = "";

        let getText = function(model, isBullet = false, text = "") {
            if (!model) {
                return text;
            }

            if (model.value) {
                let value = model.value;
                if (model.format === FormatType.PERCENT) {
                    value = value / 100;
                }
                if (model.format) {
                    value = formatter.formatValue(value, model.format, model.formatOptions);
                }
                text += value + "\n";
            }
            if (!model.text) {
                for (let key in model) {
                    if (typeof model[key] === "object" && model[key] !== null) {
                        text += getText(model[key], isBullet);
                    }
                }
                return text;
            } else {
                if (!model.text.blocks && !model.blocks) {
                    text += (model.text ?? "") + "\n";
                } else {
                    for (let block of (model.blocks ?? model.text.blocks)) {
                        if (isBullet && model.text.blocks.indexOf(block) == 0) {
                            text += "- ";
                        }
                        switch (block.textStyle) {
                            case TextStyleType.HEADING:
                            case TextStyleType.HEADLINE:
                                text += "### " + block.html + "\n";
                                break;
                            case TextStyleType.TITLE:
                                text += "**" + block.html + "**";
                                if (!isBullet) {
                                    text += "\n";
                                }
                                break;
                            case TextStyleType.LABEL:
                                text += "*" + block.html + "*";
                                if (!isBullet) {
                                    text += "\n";
                                }
                                break;
                            case TextStyleType.BODY:
                            case TextStyleType.BULLET_LIST:
                            case TextStyleType.NUMBERED_LIST:
                                if (isBullet) {
                                    text += ": ";
                                }
                                text += block.html + "\n";
                                break;
                            default:
                                text += block.html + "\n";
                        }
                    }
                }
                return text;
            }
        };

        if (canvas.getHeaderElement()) {
            prompt += getText(canvas.getHeaderElement().model) + "\n\n";
        }

        let primary = canvas.getPrimaryElement();
        prompt += getText(primary.model);

        // if (primary.isInstanceOf("CollectionElement")) {
        //     for (let item of primary.itemCollection) {
        //         prompt += getText(item, true) + "\n";
        //     }
        // }

        if (canvas.getTextTrayElement()) {
            prompt += "Conclusion:" + getText(canvas.getTextTrayElement().model) + "\n";
        }

        if (canvas.slide.getMetadata()?.generatedModel?.slideImage?.prompt) {
            prompt += "\nVisual: " + canvas.slide.getMetadata().generatedModel.slideImage.prompt + "\n";
        }

        return prompt;
    }

    async regenerateIdeas(prompt, { slideType, preserveExactText = false, usePresentationAsContext = true, files = [], webSearch = false } = {}) {
        const { currentSlide } = this._state;

        this._updateState({ isGenerating: true });

        //this.promptRef.current.getMarkdown()
        const genProps = {
            slidePrompt: prompt,
            slideType,
            preserveExactText,
            usePresentationAsContext,
            files,
            webSearch
        };

        // currentSlide.update({ metadata: { ...currentSlide.getMetadata(), ...genProps } });
        console.log("Regenerating slide with state:", genProps);

        let response;
        try {
            response = await AiGenerationService.generateSlideData({
                slideId: currentSlide.id,
                presentationId: currentSlide.presentation.id,
                slideIndex: currentSlide.getIndex(),
                presentationTopic: currentSlide.presentation.get("metadata")?.topic || "",
                ...genProps
            });
        } catch (error) {
            console.error("Error generating slide data:", error);
            ShowWarningDialog({ title: "Slide AI", message: "An error occurred while generating the slide. Please try again." });
            this._updateState({ isGenerating: false });
            return;
        }

        console.log(response);

        if (!response || !response.slides || response.slides.length == 0) {
            ShowWarningDialog({ title: "Slide AI", message: "Sorry, we couldn't generate a new slide for you. Please try again." });
            this._updateState({ isGenerating: false });
            return;
        }

        const generatedIdeas = response.slides.map(slide => slide.slideData);

        currentSlide.update({
            metadata: {
                ...currentSlide.getMetadata(),
                ...genProps,
                generatedIdeas,
                hasBeenViewed: true
            }
        });

        if (this._state.currentSlide !== currentSlide) {
            return;
        }

        await this._updateState({ generatedIdeas, isGenerating: false });
        this.buildSlideModels();
    }

    async buildSlideModels() {
        const { currentSlide, generatedIdeas, slideImage } = this._state;

        let slideCategories = [];
        for (let idea of generatedIdeas) {
            let slideModel = idea.slideModel;

            // copy the slide image from the current slide metadata
            // slideModel.slideImage = currentSlide.getMetadata().generatedModel?.slideImage;
            slideModel.slideImage = slideImage;
            // slideModel.imageStylePrompt = currentSlide.getMetadata().imageStylePrompt;

            console.log("Category", idea.slideCategory);
            console.log("Reasoning:", idea.slideReasoning);
            console.log(slideModel);

            // build slide models for this category
            let category = CategoryController.getCategory(idea.slideCategory);
            let models = category.getSlideModels(slideModel);

            // copy over metadata from the current slide
            models = models.map(model => ({
                ...model,
                metadata: {
                    ...currentSlide.getMetadata(),
                    ...model.metadata,
                    generatedIdeas,
                    aiGenerated: true,
                    // slidePrompt: slidePrompt,
                    slideCategory: idea.slideCategory,
                    slideReasoning: idea.slideReasoning,
                    slideConfidence: idea.slideConfidence
                }
            }));

            models.forEach(model => {
                let elementsWithImages = findElementsWithImages(model);
                for (let element of elementsWithImages) {
                    if (!element.content_value && element.__generateImage) {
                        element.__generateImage.isGenerating = true;
                    }
                }
            });

            slideCategories.push({
                slideCategory: category.name,
                slideReasoning: idea.slideReasoning,
                slideConfidence: idea.slideConfidence,
                models
            });
        }

        slideCategories.forEach(category => {
            category.models.forEach((model, idx) => {
                let elementsWithImages = findElementsWithImages(model);
                if (elementsWithImages.some(element => element.__generateImage.isGenerating && !element.__generateImage.isSlideImage)) {
                    AiGenerationService.generateImagesForSlide({ slide: model })
                        .then(({ slide }) => {
                            if (this._state.currentSlide !== currentSlide) {
                                return;
                            }

                            category.models[idx] = slide;
                            this._updateState({ slideCategories });
                        });
                }
            });
        });

        this._updateState({ slideCategories });
    }

    async regenerateSlideImage(imagePrompt, imageStyle) {
        let { currentSlide, slideImage, slideCategories: currentSlideCategories } = this._state;

        if (!imagePrompt) {
            return;
        }

        imageStyle = imageStyle || slideImage?.imageStyle || "";

        await currentSlide.resetSlideImage();

        let slideCategories = _.cloneDeep(currentSlideCategories);
        slideCategories.forEach(category => {
            category.models = category.models.map(model => resetSlideImage(model));
        });

        await this._updateState({
            slideImage: {
                ...slideImage,
                assetId: null,
                isGenerating: true
            },
            slideCategories
        });

        let { slide } = await AiGenerationService.regenerateSlideImage({
            slide: currentSlide.attributes,
            imagePrompt,
            imageStyle
        });

        currentSlide.update(slide);
        await currentSlide.updatePromise;

        let newSlideImage = currentSlide.getMetadata().generatedModel?.slideImage;

        slideCategories = _.cloneDeep(slideCategories);
        slideCategories.forEach(category => {
            category.models = category.models.map(model => applySlideImage(model, newSlideImage));
        });

        if (this._state.currentSlide !== currentSlide) {
            return;
        }

        await this._updateState({
            slideImage: newSlideImage,
            slideCategories
        });
    }

    // updateSlideImage(slideImage) {
    //     let { slideCategories: currentSlideCategories } = this._state;
    //
    //     slideCategories = _.cloneDeep(slideCategories);
    //     slideCategories.forEach(category => {
    //         category.models = category.models.map(model => applySlideImage(model, newSlideImage));
    //     });
    //
    //     this._updateState({ slideImage });
    // }

    /*
    renderSlideModels = async (generatedSlides, slidePrompt) => {
        const { currentSlide } = this.props;

        let slideCategories = [];
        for (let slide of generatedSlides) {
            let slideModel = slide.slideData;
            slideModel.slideImage = currentSlide.getMetadata().generatedModel?.slideImage;
            slideModel.imageStylePrompt = currentSlide.getMetadata().imageStylePrompt;

            console.log("Category", slideModel.slideCategory);
            console.log("Reasoning:", slideModel.slideReasoning);
            console.log(slideModel);

            let category = CategoryController.getCategory(slideModel.slideCategory);
            let models = category.getSlideModels(slideModel);

            // copy over metadata from the generated slide data
            models = models.map(model => ({
                ...model,
                metadata: {
                    ...currentSlide.getMetadata(),
                    ...model.metadata,
                    generatedSlides,
                    aiGenerated: true,
                    slidePrompt: slidePrompt,
                    slideCategory: slideModel.slideCategory,
                    slideReasoning: slideModel.slideReasoning,
                    slideConfidence: slideModel.slideConfidence
                }
            }));

            models.forEach(model => {
                let elementsWithImages = findElementsWithImages(model);
                for (let element of elementsWithImages) {
                    if (!element.content_value && element.__generateImage) {
                        element.__generateImage.isGenerating = true;
                    }
                }
            });

            slideCategories.push({
                slideCategory: slideModel.slideCategory,
                slideReasoning: slideModel.slideReasoning,
                slideConfidence: slideModel.slideConfidence,
                models
            });
        }

        await this.setStateAsync({
            slideCategories,
            isGenerating: false
        });

        for (let cat of slideCategories) {
            let models = cat.models;
            for (let idx = 0; idx < models.length; idx++) {
                let { slide } = await AiGenerationService.generateImagesForSlide({
                    slide: models[idx]
                });

                if (this.state.slideCategories !== slideCategories || this.hasBeenUnmounted) {
                    // If the preview models have changed or the component has been unmounted, stop generation
                    break;
                }

                let { update, hasUpdates } = computeChangeSet(models[idx], slide);
                if (hasUpdates) {
                    let updatedModel = mergeChanges(models[idx], update);
                    this.setState(state => {
                        // Keep the array reference, only replace a single model
                        const slideCategories = state.slideCategories;
                        slideCategories[slideCategories.indexOf(cat)].models[idx] = updatedModel;
                        return {
                            ...state,
                            slideCategories
                        };
                    });
                }
            }
        }
    }
     */
}

const slideAiController = new SlideAiController(initialState);
export default slideAiController;

